import React from 'react';
import { MapPin } from 'lucide-react';
import { SAMPLE_COUNT, TRUST_ACCURACY_M } from '../../lib/preciseLocation';

/**
 * Small full-screen overlay shown while we collect 5 GPS samples for tight
 * inside/outside-geofence validation. Non-dismissible — the caller controls
 * its lifecycle via `getPreciseLocation()`.
 *
 * Props:
 *   • taken   — number of samples received so far (0..SAMPLE_COUNT)
 *   • lastAccuracy — last sample's accuracy in metres (may be null)
 *   • action  — short verb shown to the user, e.g. "Checking in"
 */
export default function LocationVerifyingModal({ taken = 0, lastAccuracy = null, action = 'Verifying location' }) {
  const pct = Math.min(100, Math.round((taken / SAMPLE_COUNT) * 100));
  const trusted = lastAccuracy != null && lastAccuracy <= TRUST_ACCURACY_M;
  return (
    <div
      className="fixed inset-0 bg-black/70 z-[210] flex items-center justify-center p-4"
      data-testid="location-verifying-modal"
    >
      <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white px-6 py-5">
          <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-wider font-semibold">
            <MapPin size={14} /> {action}
          </div>
          <p className="text-white text-lg font-semibold mt-1">Verifying your location…</p>
          <p className="text-white/80 text-xs mt-1">
            Taking {SAMPLE_COUNT} GPS readings for accuracy. Please don’t move.
          </p>
        </div>

        <div className="px-6 py-6">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm text-gray-600">Sample {Math.min(taken, SAMPLE_COUNT)} of {SAMPLE_COUNT}</span>
            <span className="text-sm font-semibold text-indigo-700">{pct}%</span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-blue-600 transition-all duration-300"
              style={{ width: `${pct}%` }}
              data-testid="location-verifying-progress"
            />
          </div>

          {lastAccuracy != null && (
            <p className={`mt-4 text-xs text-center ${trusted ? 'text-emerald-700' : 'text-amber-700'}`}>
              Last fix accuracy: <strong>±{Math.round(lastAccuracy)} m</strong>{' '}
              {trusted ? '· good signal' : '· searching for better signal…'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
